// VIBRANIAM-SPACE v2 - PresetPanel.jsx
// Modal for the preset library (built-in, read-only) and for saving/loading/
// renaming/deleting the user's own named snapshots of the current graph.

import React, { useEffect, useRef, useState } from 'react';
import usePresets from '../hooks/usePresets.js';
import { BUILTIN_PRESETS } from '../presets/index.js';

function timeAgo(ts) {
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return 'adesso';
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  return `${Math.floor(s / 86400)} g fa`;
}

function CustomPresetRow({ preset, onLoad, onRename, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(preset.name);
  const inputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== preset.name) onRename(preset.id, draft.trim());
    else setDraft(preset.name);
  };

  return (
    <div className="flex items-center gap-1.5 rounded border border-white/5 bg-ink-700/60 px-2 py-1.5 hover:border-white/15">
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setDraft(preset.name); setEditing(false); }
          }}
          className="min-w-0 flex-1 rounded bg-ink-600 px-1.5 py-0.5 text-[11px] text-slate-100 outline-none focus:ring-1 focus:ring-vibe-cyan"
        />
      ) : (
        <button
          type="button"
          onClick={() => onLoad(preset)}
          title="Carica questo preset"
          className="min-w-0 flex-1 truncate text-left text-[11px] text-slate-200 hover:text-vibe-cyan"
        >
          {preset.name}
        </button>
      )}
      <span className="shrink-0 font-mono text-[9px] text-slate-600">{timeAgo(preset.updatedAt)}</span>
      <button type="button" title="Rinomina" onClick={() => setEditing((v) => !v)}
        className="shrink-0 rounded px-1 text-[10px] text-slate-500 hover:bg-white/10 hover:text-slate-200">
        ✎
      </button>
      <button type="button" title="Elimina" onClick={() => onRemove(preset.id)}
        className="shrink-0 rounded px-1 text-[10px] text-slate-500 hover:bg-red-500/20 hover:text-red-300">
        ×
      </button>
    </div>
  );
}

export default function PresetPanel({ open, onClose, graph, onLoad }) {
  const { presets, save, rename, remove } = usePresets();
  const [name, setName] = useState('');
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSave = () => {
    const res = save(name, graph);
    if (!res.ok) { setFeedback({ ok: false, text: res.error }); return; }
    setFeedback({ ok: true, text: res.overwritten ? `"${res.preset.name}" sovrascritto.` : `"${res.preset.name}" salvato.` });
    setName('');
  };

  const loadAndClose = (preset) => {
    onLoad(preset.graph);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="node-shadow flex max-h-[80vh] w-[440px] flex-col rounded-lg border border-white/10 bg-ink-800">
        <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-3 py-2.5">
          <h2 className="text-[12px] font-semibold uppercase tracking-widest text-slate-200">Preset</h2>
          <button type="button" onClick={onClose}
            className="rounded px-1.5 py-0.5 text-[13px] text-slate-400 hover:bg-white/10 hover:text-slate-100">×</button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Libreria ({BUILTIN_PRESETS.length})
          </div>
          {BUILTIN_PRESETS.length === 0 ? (
            <p className="mb-3 text-[10px] text-slate-600">Nessun preset integrato trovato.</p>
          ) : (
            <div className="mb-4 space-y-1">
              {BUILTIN_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => loadAndClose(p)}
                  title={p.description || p.name}
                  className="flex w-full items-center justify-between gap-2 rounded border border-white/5 bg-ink-700/60 px-2 py-1.5 text-left hover:border-vibe-cyan/40 hover:bg-ink-700"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[11px] text-slate-200">{p.name}</span>
                    {p.description && (
                      <span className="block truncate text-[9px] text-slate-500">{p.description}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-[9px] uppercase tracking-wide text-vibe-cyan/70">carica</span>
                </button>
              ))}
            </div>
          )}

          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            I miei preset ({presets.length})
          </div>
          {presets.length === 0 ? (
            <p className="mb-2 text-[10px] text-slate-600">Nessun preset salvato ancora.</p>
          ) : (
            <div className="space-y-1">
              {presets.map((p) => (
                <CustomPresetRow key={p.id} preset={p} onLoad={loadAndClose} onRename={rename} onRemove={remove} />
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-white/10 px-3 py-2.5">
          {feedback && (
            <div className={`mb-1.5 text-[10px] ${feedback.ok ? 'text-vibe-lime' : 'text-red-300'}`}>
              {feedback.text}
            </div>
          )}
          <div className="flex gap-1.5">
            <input
              value={name}
              onChange={(e) => { setName(e.target.value); setFeedback(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              placeholder="nome del preset..."
              className="min-w-0 flex-1 rounded bg-ink-600 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:ring-1 focus:ring-vibe-cyan"
            />
            <button
              type="button"
              onClick={handleSave}
              className="shrink-0 rounded bg-vibe-cyan px-3 py-1.5 text-[11px] font-semibold text-ink-900 transition hover:brightness-110"
            >
              Salva patch attuale
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
